import { ArrowRightOutlined, FilePdfOutlined, SaveOutlined, SyncOutlined } from "@ant-design/icons";
import { Button, Card, Checkbox, Form, Input, message, Spin, Tooltip } from "antd";
import { createSearchTemplate } from "api/onboarding.api";
import { exportToPDF } from "components/Common/PdfExport/pdfExportUtils";
import React, { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

interface SecondStepOnboardingFormProps {
  initialData: any; // The data passed from the first step
  onNext: (response: any) => void; // Passes the updated data to the next step
}

const SecondStepOnboardingForm: React.FC<SecondStepOnboardingFormProps> = ({
  onNext,
  initialData,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState(""); // Local state for ReactQuill
  const editorRef = useRef<HTMLDivElement | null>(null);

  const modules = {
    toolbar: [
      // Basic styling options
      [{ header: [1, 2, 3, false] }], // Header dropdown
      [{ font: [] }], // Font family dropdown
      [{ size: [] }], // Font size dropdown
      ["bold", "italic", "underline", "strike"], // Text styles
      [{ color: [] }, { background: [] }], // Text and background color
      [{ script: "sub" }, { script: "super" }], // Subscript / superscript

      // Alignment options
      [{ align: [] }],

      // Lists
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }], // Indent / Outdent

      // Block options
      [{ direction: "rtl" }], // Right-to-left text direction
      ["blockquote", "code-block"], // Blockquote and code block

      // Links, images, and videos
      ["link", "image", "video"],

      // Clear formatting
      ["clean"], // Remove formatting
    ],
  };

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "script",
    "align",
    "list",
    "bullet",
    "indent",
    "direction",
    "blockquote",
    "code-block",
    "link",
    "image",
    "video",
  ];

  const removeHtmlTags = (input: string): string => {
    return input.replace(/<\/?[^>]+(>|$)/g, "");
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      const templateName = form.getFieldValue("template_name");
      const description = form.getFieldValue("description");
      const isTemplateChecked = form.getFieldValue("save_as_template");

      formData.append(
        "job_title",
        JSON.stringify(initialData?.data.makeJd.searchConfig.jobTitles || [])
      );
      formData.append(
        "industries",
        JSON.stringify(initialData?.data.makeJd.searchConfig.industries || [])
      );
      formData.append(
        "keywords",
        JSON.stringify(initialData?.data.makeJd.searchConfig.keywords || [])
      );
      formData.append(
        "locations",
        JSON.stringify(initialData?.data.makeJd.searchConfig.locations || [])
      );
      formData.append(
        "locations_excluded",
        JSON.stringify(initialData?.data.makeJd.searchConfig.locationsExclude || [])
      );
      formData.append(
        "recruiting_company",
        JSON.stringify(initialData?.data.makeJd.searchConfig.companies || [])
      );
      formData.append(
        "companies_excluded",
        JSON.stringify(initialData?.data.makeJd.searchConfig.companiesExclude || [])
      );
      formData.append(
        "min_experience",
        initialData?.data.makeJd.searchConfig.experienceMin?.toString() || 0
      );
      formData.append(
        "max_experience",
        initialData?.data.makeJd.searchConfig.experienceMax?.toString() || 0
      );
      formData.append(
        "minimum_salary",
        initialData?.data.makeJd.searchConfig.salaryMin?.toString() || 0
      );
      formData.append(
        "maximum_salary",
        initialData?.data.makeJd.searchConfig.salaryMax?.toString() || 0
      );
      formData.append("description", removeHtmlTags(description));
      formData.append("currency", "USD");
      formData.append("template", "false");
      formData.append("user", "1");
      formData.append("job", "73");
      formData.append("is_talent_pool", "false");
      if (isTemplateChecked) {
        if (!templateName) {
          message.error("Please enter a template name.");
          return;
        } else {
          formData.append("search_title", templateName);
        }
      } else {
        formData.append("search_title", initialData?.data.makeJd.searchConfig.jobTitles[0]);
      }
      //   await createSearchTemplate(formData);
      //   message.success("Your data has been saved successfully!");
      onNext(formData); // Pass updated values to the next step
    } catch (errorInfo) {
      message.error("Validation Failed");
    } finally {
      setLoading(false);
    }
  };

  const handleQuillChange = (value: string) => {
    // const sanitizedValue = value.replace(/<p><br><\/p>/g, "<br>").trim();
    setDescription(value);
    form.setFieldsValue({ description: value });
    form.validateFields(["description"]);
  };

  const handleRegenerate = () => {
    console.log("Regenerate logic triggered");
  };

  const handleExportAsPDF = () => {
    const templateName = form.getFieldValue("template_name");
    const isTemplateChecked = form.getFieldValue("save_as_template");

    // Validation logic
    if (isTemplateChecked && !templateName) {
      message.error("Please enter a template name.");
      return;
    }

    if (!editorRef.current) {
      console.error("Editor content is not available.");
      message.error("Editor content is not ready.");
      return;
    }

    const onBoardingDescriptionResponse = {
      searchConfig: initialData?.data?.makeJd?.searchConfig || {},
      description: form.getFieldValue("description"), // Map description correctly
    };

    exportToPDF({
      editorRef,
      onBoardingDescriptionResponse,
      templateName: templateName || "",
      isTemplateChecked: !!isTemplateChecked,
    });
  };

  useEffect(() => {
    if (initialData) {
      const jobTitles = initialData?.data?.makeJd?.searchConfig?.jobTitles || [];
      const description = initialData?.data?.makeJd?.description || "";
      // Populate form fields with initialData
      form.setFieldsValue({
        template_name: jobTitles[0] || "",
        description: description || "",
        save_as_template: false,
      });
      setDescription(description); // Update local state for ReactQuill
    }
  }, [initialData, form]);

  return (
    <>
      <Form form={form} onFinish={handleSave} className="mb-3">
        <Form.Item
          label="Template Name"
          name="template_name"
          style={{ marginBottom: 0, width: "60%", marginTop: "10px" }}
        >
          <Input placeholder="Template Name" />
        </Form.Item>
        <div className="custom-card" style={{ marginTop: "2%" }}>
          <Form.Item
            name="description"
            rules={[{ required: true, message: "Please input the description!" }]}
          >
            <Card bordered={false}>
              <ReactQuill
                value={description} // Controlled value from local state
                modules={modules}
                formats={formats}
                onChange={handleQuillChange}
                ref={(instance) => {
                  if (instance && instance.getEditor) {
                    editorRef.current = instance.getEditor().root;
                  }
                }}
              />
              {/* <Tooltip title="This is a hint text to help user." placement="bottom">
                                <p style={{ color: "gray", marginTop: "10px" }}>
                                    This is a hint text to help user.
                                </p>
                            </Tooltip> */}
            </Card>
          </Form.Item>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "10px",
          }}
        >
          {/* <Form.Item name="save_as_template" valuePropName="checked" style={{ marginBottom: 0 }}>
            <Checkbox>Save as template</Checkbox>
          </Form.Item> */}
          <div style={{ display: "flex", gap: "10px" }}>
            <Button onClick={handleRegenerate}>
              <SyncOutlined style={{ fontSize: "20px", color: "darkgray" }} /> Regenerate
            </Button>
            <Button onClick={handleExportAsPDF}>
              <FilePdfOutlined style={{ fontSize: "24px", color: "#e74c3c" }} /> Export as PDF
            </Button>
            <Button
              // type="primary" 
              htmlType="submit"
              className="text-white py-1 px-3 rounded-md bg-[#845dda] border border-[#7F56D9]"
            >
              {loading ? (
                <Spin spinning={loading} size="small" className="custom-spin" />
              ) : (
                <SaveOutlined />
              )}
              Save & Next <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      </Form>
    </>
  );
};

export default SecondStepOnboardingForm;
